import { api } from '@/lib/axios'
import type {
  NewsSearchRequest,
  NewsSearchResponse,
  KeywordNewsResult,
  BackendSearchRequest,
  BackendSearchResponse,
  BackendBatchSearchRequest,
  BackendBatchSearchResponse,
  BackendNewsItem,
} from '../types/news.types'
import { searchCache } from '../utils/searchCache'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// News API Client with Caching
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const newsApi = {
  /**
   * Search news by multiple keywords using batch API endpoint
   * @param request - Search request parameters
   * @returns News search response with results for all keywords
   */
  searchNews: async (request: NewsSearchRequest): Promise<NewsSearchResponse> => {
    const { keywords, display = 100, sort = 'sim', start = 1 } = request

    // Errors are intentionally not caught here. Swallowing them and returning empty
    // results made every outage — quota exhaustion, expired auth, backend down — look
    // like "검색 결과가 없습니다", and left useNewsSearch's onError permanently
    // unreachable. Per-keyword failures are still handled below via `data.errors`.

    // Use batch API endpoint for better performance
    const { data } = await api.post<BackendBatchSearchResponse>('/api/v1/news/search/batch', {
      keywords,
      display,
      start,
      sort,
    } as BackendBatchSearchRequest)

    // Transform batch response to expected format
    const results: KeywordNewsResult[] = Object.entries(data.results).map(([keyword, result]) => ({
      keyword,
      items: result.items.map((item: BackendNewsItem) => ({
        title: item.title,
        link: item.link,
        originallink: item.originallink,
        description: item.description,
        pub_date: item.pubDate,
      })),
      total: result.total,
      start: result.start,
      display: result.display,
    }))

    // Add empty results for failed keywords
    Object.entries(data.errors).forEach(([keyword, error]) => {
      console.error(`Failed to search keyword: ${keyword}`, error)
      // Only add if not already in results
      if (!results.find((r) => r.keyword === keyword)) {
        results.push({
          keyword,
          items: [],
          total: 0,
          start: 1,
          display,
        })
      }
    })

    // Log batch statistics
    if (data.error_count > 0) {
      console.warn(
        `Batch search completed with ${data.error_count} error(s) out of ${data.total_keywords} keyword(s)`
      )
    }

    return {
      results,
      total_results: results.reduce((sum: number, r: KeywordNewsResult) => sum + r.total, 0),
      search_params: {
        keywords,
        display,
        sort,
      },
    }
  },

  /**
   * Search news by single keyword with caching
   * @param keyword - Keyword to search
   * @param options - Additional search options
   * @returns News results for the keyword
   */
  searchByKeyword: async (
    keyword: string,
    options?: {
      display?: number
      sort?: 'sim' | 'date'
      start?: number
    }
  ): Promise<KeywordNewsResult> => {
    const display = options?.display || 10
    const sort = options?.sort || 'sim'
    const start = options?.start || 1

    // Check cache first
    const cacheParams = { display, sort, start }
    const cached = searchCache.get(keyword, cacheParams)
    if (cached) {
      console.log(`[Cache HIT] Returning cached results for: ${keyword}`)
      return cached
    }

    console.log(`[Cache MISS] Fetching from API for: ${keyword}`)

    // Fetch from API
    const { data } = await api.post<BackendSearchResponse>('/api/v1/news/search', {
      keyword,
      display,
      start,
      sort,
    } as BackendSearchRequest)

    const result: KeywordNewsResult = {
      keyword,
      items: data.items.map((item: BackendNewsItem) => ({
        title: item.title,
        link: item.link,
        originallink: item.originallink,
        description: item.description,
        pub_date: item.pubDate,
      })),
      total: data.total,
      start: data.start,
      display: data.display,
    }

    // Store in cache
    searchCache.set(keyword, result, cacheParams)

    return result
  },

  /**
   * Get paginated news for a keyword
   * @param keyword - Keyword to search
   * @param page - Page number (1-based)
   * @param display - Items per page
   * @param sort - Sort method
   * @returns News results for the keyword
   */
  getPaginatedNews: async (
    keyword: string,
    page: number,
    display: number,
    sort: 'sim' | 'date' = 'sim'
  ): Promise<KeywordNewsResult> => {
    const start = (page - 1) * display + 1
    return newsApi.searchByKeyword(keyword, { display, sort, start })
  },
}
